import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  FileSpreadsheet,
  File as FileIcon,
} from 'lucide-react';
import type { AnnouncementAttachment } from '@/lib/queries';

/** Formatea bytes a una etiqueta legible. */
function formatBytes(n?: number): string {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/** Devuelve icono + estilo según el tipo de archivo. */
function fileMeta(mimeType: string, name: string) {
  const m = (mimeType || '').toLowerCase();
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (m.includes('pdf') || ext === 'pdf')
    return { Icon: FileText, color: 'text-error', bg: 'bg-red-50', label: 'PDF' };
  if (m.includes('word') || ext === 'doc' || ext === 'docx')
    return { Icon: FileText, color: 'text-info', bg: 'bg-blue-50', label: 'Word' };
  if (m.includes('sheet') || m.includes('excel') || ext === 'xls' || ext === 'xlsx' || ext === 'csv')
    return { Icon: FileSpreadsheet, color: 'text-accent', bg: 'bg-accent-light', label: 'Excel' };
  if (m.startsWith('text/') || ext === 'txt')
    return { Icon: FileText, color: 'text-font-secondary', bg: 'bg-bg', label: 'Texto' };
  return { Icon: FileIcon, color: 'text-font-secondary', bg: 'bg-bg', label: ext.toUpperCase() || 'Archivo' };
}

/* ============================ Lightbox / carrusel ============================ */

function Lightbox({
  images,
  index,
  onClose,
  onIndex,
}: {
  images: AnnouncementAttachment[];
  index: number;
  onClose: () => void;
  onIndex: (i: number) => void;
}) {
  const go = useCallback(
    (dir: number) => onIndex((index + dir + images.length) % images.length),
    [index, images.length, onIndex],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [go, onClose]);

  const img = images[index];

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/90 anim-fade-in">
      {/* Cerrar */}
      <button
        onClick={onClose}
        className="absolute right-4 top-safe z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
        aria-label="Cerrar"
      >
        <X size={20} />
      </button>

      {/* Contador */}
      <div className="absolute left-1/2 top-safe -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white">
        {index + 1} / {images.length}
      </div>

      {/* Prev / Next */}
      {images.length > 1 && (
        <>
          <button
            onClick={() => go(-1)}
            className="absolute left-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label="Anterior"
          >
            <ChevronLeft size={24} />
          </button>
          <button
            onClick={() => go(1)}
            className="absolute right-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            aria-label="Siguiente"
          >
            <ChevronRight size={24} />
          </button>
        </>
      )}

      {/* Imagen (click fuera = cerrar) */}
      <div className="absolute inset-0" onClick={onClose} />
      <AnimatePresence mode="wait">
        <motion.img
          key={img.s3Key}
          src={img.url}
          alt={img.name}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="relative z-[1] max-h-[88vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        />
      </AnimatePresence>

      {/* Tira de miniaturas */}
      {images.length > 1 && (
        <div className="absolute bottom-safe left-1/2 z-10 flex max-w-[90vw] -translate-x-1/2 gap-2 overflow-x-auto rounded-lg bg-white/10 p-2">
          {images.map((im, i) => (
            <button
              key={im.s3Key}
              onClick={() => onIndex(i)}
              className={`h-12 w-12 flex-shrink-0 overflow-hidden rounded transition ${
                i === index ? 'ring-2 ring-white' : 'opacity-60 hover:opacity-100'
              }`}
            >
              <img src={im.url} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================ Collage de imágenes ============================ */

function ImageTile({
  img,
  onClick,
  onError,
  overlay,
  className = '',
}: {
  img: AnnouncementAttachment;
  onClick: () => void;
  onError: (s3Key: string) => void;
  overlay?: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative overflow-hidden bg-bg ${className}`}
    >
      <img
        src={img.url}
        alt={img.name}
        loading="lazy"
        onError={() => onError(img.s3Key)}
        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
      />
      {overlay && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/55 text-2xl font-bold text-white">
          {overlay}
        </div>
      )}
    </button>
  );
}

function ImageCollage({
  images,
  onOpen,
  onError,
}: {
  images: AnnouncementAttachment[];
  onOpen: (i: number) => void;
  onError: (s3Key: string) => void;
}) {
  const n = images.length;

  if (n === 1) {
    return (
      <div className="overflow-hidden rounded-xl border border-border">
        <ImageTile
          img={images[0]}
          onClick={() => onOpen(0)}
          onError={onError}
          className="max-h-[460px] w-full"
        />
      </div>
    );
  }

  if (n === 2) {
    return (
      <div className="grid h-72 grid-cols-2 gap-1 overflow-hidden rounded-xl border border-border">
        <ImageTile img={images[0]} onClick={() => onOpen(0)} onError={onError} className="h-full" />
        <ImageTile img={images[1]} onClick={() => onOpen(1)} onError={onError} className="h-full" />
      </div>
    );
  }

  // 3 o más: imagen grande a la izquierda + columna de 2 a la derecha.
  // Si hay más de 3, la última visible muestra "+N".
  const extra = n - 3;
  return (
    <div className="grid h-80 grid-cols-2 gap-1 overflow-hidden rounded-xl border border-border">
      <ImageTile
        img={images[0]}
        onClick={() => onOpen(0)}
        onError={onError}
        className="row-span-2 h-full"
      />
      <ImageTile img={images[1]} onClick={() => onOpen(1)} onError={onError} className="h-full" />
      <ImageTile
        img={images[2]}
        onClick={() => onOpen(2)}
        onError={onError}
        overlay={extra > 0 ? `+${extra}` : undefined}
        className="h-full"
      />
    </div>
  );
}

/* ============================ Componente principal ============================ */

export function AnnouncementMedia({
  attachments,
}: {
  attachments?: AnnouncementAttachment[];
}) {
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [failed, setFailed] = useState<Set<string>>(new Set());
  const markFailed = (s3Key: string) =>
    setFailed((prev) => {
      const next = new Set(prev);
      next.add(s3Key);
      return next;
    });

  if (!attachments || attachments.length === 0) return null;

  // Se excluyen las imágenes que fallaron al cargar (objeto faltante en S3, etc.)
  const images = attachments.filter((a) => a.kind === 'image' && !failed.has(a.s3Key));
  const files = attachments.filter((a) => a.kind !== 'image');

  if (images.length === 0 && files.length === 0) return null;

  return (
    <div className="mt-6 flex flex-col gap-4">
      {images.length > 0 && (
        <ImageCollage images={images} onOpen={(i) => setLightbox(i)} onError={markFailed} />
      )}

      {files.length > 0 && (
        <div className="flex flex-col gap-2">
          {files.map((f) => {
            const { Icon, color, bg, label } = fileMeta(f.mimeType, f.name);
            return (
              <a
                key={f.s3Key}
                href={f.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 rounded-xl border border-border bg-white p-3 transition-all hover:border-primary-light hover:shadow-sm lift"
              >
                <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg ${bg} ${color}`}>
                  <Icon size={20} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-font-primary">{f.name}</p>
                  <p className="text-xs text-font-tertiary">
                    {label}
                    {f.size ? ` · ${formatBytes(f.size)}` : ''}
                  </p>
                </div>
                <span className="flex h-9 items-center gap-1.5 rounded-md border border-primary/30 bg-primary-subtle px-3 text-xs font-semibold text-primary">
                  <Download size={14} /> Descargar
                </span>
              </a>
            );
          })}
        </div>
      )}

      {lightbox !== null && images.length > 0 && (
        <Lightbox
          images={images}
          index={Math.min(lightbox, images.length - 1)}
          onClose={() => setLightbox(null)}
          onIndex={setLightbox}
        />
      )}
    </div>
  );
}
